<script setup lang="ts">

import type { ContractData } from '@/contracts/types/types';
import { PaymentTypeOptions } from '@/contracts/types/types';
import {
  DsForm,
  DsFormGroup,
  DsInput,
  DsSelect,
  DsTextarea,
  DsAlert,
} from '@demvsystems/design-components';
import MoneyInput from '@/application/components/form-elements/MoneyInput/MoneyInput.vue';
import DateInput from '@/application/components/form-elements/DateInput/DateInput.vue';
import { toOptions } from '@/application/composables/vselect';
import { useGesellschaften } from '@/application/composables/gesellschaften';
import { useDetectMobile } from '@/application/composables/detectMobile';
import { useSparten } from '@/documents/composables/sparten';
import { computed } from 'vue';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import { storeToRefs } from 'pinia';
import { useAiDocumentExtractionStore } from '@/documents/store/aiDocumentExtractionStore.ts';
import AiIndicator from '@/application/components/AiIndicator.vue';
import { isBefore, isEqual, differenceInDays, parse } from 'date-fns';

interface Props {
  modelValue: ContractData,
  updateField: () => void,
  validationErrors?: Record<string, string[]>,
  updateForm?: boolean,
}

const props = defineProps<Props>();

const { isMobile } = useDetectMobile();
const { sparten, fetchSparten } = useSparten();
const { gesellschaften, fetchGesellschaften } = useGesellschaften();
const { duzen } = useBaseDataStore();
const { extract } = storeToRefs(useAiDocumentExtractionStore());

const { isKfzSparte } = useGesellschaften();
const isKfz = computed(() => isKfzSparte(props.modelValue.sparte_id));
const gesellschaftOptions = toOptions(gesellschaften, 'name', 'id');

// The Kennzeichen is a single inline row of inputs, so collect the per-part validation
// errors (kennzeichen.unterscheidungszeichen, .erkennungszeichen, ...) under one key that
// the Kennzeichen form group can display.
const validationErrorsWithKennzeichen = computed<Record<string, string[]>>(() => {
  const errors = { ...(props.validationErrors ?? {}) };
  const kennzeichenErrors = [...new Set(
    Object.entries(errors)
      .filter(([key]) => key.startsWith('kennzeichen.'))
      .flatMap(([, messages]) => messages),
  )];

  if (kennzeichenErrors.length > 0) {
    errors.kennzeichen = kennzeichenErrors;
  }

  return errors;
});

fetchGesellschaften();
fetchSparten();

const dateWarning = computed(() => {
  const { beginn, ablauf } = extract.value;
  if (!beginn && !ablauf) return false; // if one is not set by AI response data, we don't show a warning

  const beginnDate = parse(props.modelValue.beginn ?? '', 'dd.MM.yyyy', new Date());
  const ablaufDate = parse(props.modelValue.ablaufdatum ?? '', 'dd.MM.yyyy', new Date());

  if (!beginnDate || !ablaufDate) return false;
  if (isBefore(ablaufDate, beginnDate)) return 'Das Ablaufdatum liegt vor dem Beginn.';
  if (isEqual(beginnDate, ablaufDate)) return 'Beginn und Ablaufdatum sind gleich. Wenn dies gewollt ist, einfach fortfahren.';
  if (Math.abs(differenceInDays(beginnDate, ablaufDate)) < 30) return 'Beginn und Ablaufdatum sind weniger als 30 Tage entfernt. Wenn dies gewollt ist, einfach fortfahren.';

  return false;
})
</script>

<template>
  <DsForm :validation-errors="validationErrorsWithKennzeichen">
    <div class="flex flex-col gap-4">
      <DsFormGroup
        label="Gesellschaft"
        label-for="Gesellschaft"
        validation-name="gesellschaft_id"
        :inline-reverse="!isMobile"
      >
        <template v-if="extract.gesellschaftId && extract.gesellschaftId === modelValue.gesellschaft_id && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <DsSelect
            id="Gesellschaft"
            :model-value="modelValue.gesellschaft_id"
            :data="gesellschaftOptions"
            :is-loading="gesellschaftOptions.length === 0"
            placeholder=""
            size="lg"
            class="w-full md:w-80"
            @update:model-value="updateField('gesellschaft_id', $event)"
          />
        </div>
      </DsFormGroup>

      <DsFormGroup
        label="Sparte"
        label-for="sparte_id"
        validation-name="sparte_id"
        :inline-reverse="!isMobile"
      >
        <template v-if="extract.sparteId && extract.sparteId === modelValue.sparte_id && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <DsSelect
            id="sparte_id"
            :model-value="modelValue.sparte_id"
            :data="sparten"
            value-key="id"
            label-key="display_name"
            placeholder=""
            size="lg"
            class="w-full md:w-80"
            @update:model-value="updateField('sparte_id', $event)"
          >
            <template #entry="{ entry }">
              <span class="text-gray-800">{{ entry.display_name }}</span>
              <span
                v-if="!!entry.type"
                class="badge text-secondary"
              >
                {{ entry.type }}
              </span>
            </template>
          </DsSelect>
        </div>
      </DsFormGroup>

      <DsFormGroup
        label="Vertrags-Nr."
        label-for="vertragsnummer"
        validation-name="vertragsnummer"
        :inline-reverse="!isMobile"
        class="whitespace-nowrap"
      >
        <template v-if="extract.vertragsnummer && extract.vertragsnummer === modelValue.vertragsnummer && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <DsInput
            id="vertragsnummer"
            :model-value="modelValue.vertragsnummer"
            placeholder=""
            size="lg"
            class="w-full md:w-80"
            @update:model-value="updateField('vertragsnummer', $event)"
          />
        </div>
      </DsFormGroup>

      <DsFormGroup
        label="Beginn"
        label-for="beginn"
        validation-name="beginn"
        :inline-reverse="!isMobile"
      >
        <template v-if="extract.beginn && extract.beginn === modelValue.beginn && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <DateInput
            id="beginn"
            :model-value="modelValue.beginn"
            placeholder=""
            size="lg"
            class="w-full md:w-80"
            @update:model-value="updateField('beginn', $event)"
          />
        </div>
      </DsFormGroup>

      <DsFormGroup
        label="Ablaufdatum"
        label-for="ablaufdatum"
        validation-name="ablaufdatum"
        :inline-reverse="!isMobile"
      >
        <template v-if="extract.ablauf && extract.ablauf === modelValue.ablaufdatum && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <DateInput
            id="ablaufdatum"
            :model-value="modelValue.ablaufdatum"
            size="lg"
            class="w-full md:w-80"
            @update:model-value="updateField('ablaufdatum', $event)"
          />
        </div>
      </DsFormGroup>

      <DsAlert v-if="dateWarning" type="warning">{{ dateWarning }}</DsAlert>

      <DsFormGroup
        label="Beitrag"
        label-for="Beitrag"
        validation-name="beitrag"
        :inline-reverse="!isMobile"
      >
        <template v-if="extract.beitrag && extract.beitrag === modelValue.beitrag && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <MoneyInput
            id="Beitrag"
            :model-value="modelValue.beitrag"
            size="lg"
            class="w-full md:w-80"
            :disabled="updateForm"
            @update:model-value="updateField('beitrag', $event)"
          />
        </div>
      </DsFormGroup>

      <DsFormGroup
        label="Zahlweise"
        label-for="Zahlweise"
        validation-name="zahlweise"
        :inline-reverse="!isMobile"
      >
        <template v-if="extract.zahlweise && extract.zahlweise === modelValue.zahlweise && !updateForm" #label-end>
          <AiIndicator />
        </template>

        <div class="w-full flex justify-end">
          <DsSelect
            id="Zahlweise"
            :model-value="modelValue.zahlweise"
            :data="PaymentTypeOptions"
            placeholder=""
            size="lg"
            class="w-full md:w-80"
            @update:model-value="updateField('zahlweise', $event)"
          />
        </div>
      </DsFormGroup>

      <DsFormGroup
        v-if="isKfz"
        label="Kennzeichen"
        validation-name="kennzeichen"
        :inline-reverse="!isMobile"
      >
        <div class="flex justify-between md:justify-end mx-auto lg:mx-0 w-full gap-1">
          <DsInput
            :model-value="modelValue.kennzeichen.unterscheidungszeichen"
            :disabled="updateForm"
            size="lg"
            :mask="{
              mask: 'AAA',
              tokens: { A: { pattern: /[a-zA-ZäöüÄÖÜ]/, uppercase: true } },
            }"
            style="max-width: 74px"
            @update:model-value="updateField('kennzeichen.unterscheidungszeichen', $event)"
          />
          <DsInput
            :model-value="modelValue.kennzeichen.erkennungszeichen"
            :disabled="updateForm"
            size="lg"
            :mask="{
              mask: 'AA',
              tokens: { A: { pattern: /[a-zA-ZäöüÄÖÜ]/, uppercase: true } },
            }"
            style="max-width: 56px"
            @update:model-value="updateField('kennzeichen.erkennungszeichen', $event)"
          />
          <DsInput
            :model-value="modelValue.kennzeichen.erkennungsnummer"
            :disabled="updateForm"
            size="lg"
            mask="####"
            style="max-width: 78px"
            @update:model-value="updateField('kennzeichen.erkennungsnummer', $event)"
          />
          <DsSelect
            :model-value="modelValue.kennzeichen.abschliessendeszeichen"
            :disabled="updateForm"
            :data="[
              {label: 'H', value: 'H'},
              {label: 'E', value: 'E'},
            ]"
            :searchable="false"
            size="lg"
            style="min-width: 100px"
            placeholder=""
            @update:model-value="updateField('kennzeichen.abschliessendeszeichen', $event)"
          />
        </div>
      </DsFormGroup>

      <DsFormGroup
        v-if="!updateForm"
        label="Notiz für den Makler"
        label-for="notiz"
        :inline-reverse="!isMobile"
      >
        <div class="w-full md:flex md:justify-end">
          <DsTextarea
            id="notiz"
            :model-value="modelValue.notiz"
            :placeholder="`${duzen('Hinterlassen Sie', 'Hinterlasse')} eine Notiz zur Vertragsanlage ...`"
            :rows="3"
            class="w-full md:w-80 md:ml-auto"
            @update:model-value="updateField('notiz', $event)"
          />
        </div>
      </DsFormGroup>
    </div>

    <DsAlert
      v-if="isKfz && updateForm"
      type="primary"
      icon="circle-info"
      class="mt-5 text-left"
    >
      Kennzeichen können nicht bearbeitet werden. Bei Fehlern im Kennzeichen, {{ duzen('lösche', 'löschen Sie') }}
      den Vertrag und {{ duzen('lege', 'legen Sie') }} ihn neu an oder
      {{ duzen('frage deinen', 'fragen Sie Ihren') }} Makler.
    </DsAlert>
  </DsForm>
</template>
